﻿import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  CheckCheck,
  CornerUpLeft,
  FileText,
  Forward,
  MoreVertical,
  Paperclip,
  Phone,
  PhoneMissed,
  PhoneOutgoing,
  Search,
  SendHorizontal,
  StickyNote,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { queryKeys } from "@/constants/queryKeys";
import {
  chatMessageAction,
  fetchChatConversation,
  fetchChatTimeline,
  markChatRead,
  searchChatMessages,
  sendChatMessage,
  sendChatTyping,
} from "@/services/api/chat";
import { toast } from "@/components/ui";
import { useMessagesStore } from "../store/useMessagesStore";
import { ConversationAvatar } from "./ConversationAvatar";
import { ChatWelcomePanel } from "./ChatWelcomePanel";
import { ContactInfoDrawer } from "./ContactInfoDrawer";
import { ForwardMessageModal } from "./ForwardMessageModal";
import { useUiStore } from "@/store";
import { usePhoneStore } from "@/features/phone/store/usePhoneStore";
import { callBubbleLabel, formatChatTime } from "../lib/format";
import { linkifyText } from "../lib/linkify";
import type { ChatMessage, MessageReplyPreview, TimelineItem } from "../data/types";

type ChatPanelProps = {
  onBack?: () => void;
  showBack?: boolean;
};

export function ChatPanel({ onBack, showBack = false }: ChatPanelProps) {
  const activeConversationId = useMessagesStore((s) => s.activeConversationId);
  const draft = useMessagesStore((s) => s.draft);
  const setDraft = useMessagesStore((s) => s.setDraft);
  const contactInfoOpen = useMessagesStore((s) => s.contactInfoOpen);
  const setContactInfoOpen = useMessagesStore((s) => s.setContactInfoOpen);
  const toggleContactInfo = useMessagesStore((s) => s.toggleContactInfo);
  const threadSearchOpen = useMessagesStore((s) => s.threadSearchOpen);
  const setThreadSearchOpen = useMessagesStore((s) => s.setThreadSearchOpen);
  const threadSearch = useMessagesStore((s) => s.threadSearch);
  const setThreadSearch = useMessagesStore((s) => s.setThreadSearch);
  const replyTo = useMessagesStore((s) => s.replyTo);
  const setReplyTo = useMessagesStore((s) => s.setReplyTo);
  const setForwardMessage = useMessagesStore((s) => s.setForwardMessage);
  const setViewingConversationId = useMessagesStore((s) => s.setViewingConversationId);
  const peerTyping = useMessagesStore((s) => {
    const id = s.activeConversationId;
    return id ? s.typingByConversation[id] : undefined;
  });

  const openDialer = useUiStore((s) => s.openDialer);
  const setDialNumber = usePhoneStore((s) => s.setDialNumber);
  const makeCall = usePhoneStore((s) => s.makeCall);
  const regStatus = usePhoneStore((s) => s.regStatus);

  const queryClient = useQueryClient();
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingPulseRef = useRef(0);
  const lastTypingSentRef = useRef(0);

  const { data: conversation, isLoading: conversationLoading } = useQuery({
    queryKey: queryKeys.chatConversation(activeConversationId || ""),
    queryFn: () => fetchChatConversation(activeConversationId!),
    enabled: Boolean(activeConversationId),
  });

  const { data: timelineData, isLoading } = useQuery({
    queryKey: queryKeys.chatTimeline(activeConversationId || ""),
    queryFn: () => fetchChatTimeline(activeConversationId!),
    enabled: Boolean(activeConversationId),
  });

  const { data: searchHits = [] } = useQuery({
    queryKey: ["chat-thread-search", activeConversationId, threadSearch],
    queryFn: () => searchChatMessages(activeConversationId!, threadSearch),
    enabled: Boolean(activeConversationId && threadSearchOpen && threadSearch.trim()),
  });

  const sendMutation = useMutation({
    mutationFn: (payload: { body?: string; file?: File }) =>
      sendChatMessage(activeConversationId!, {
        ...payload,
        reply_to: replyTo?.id,
        client_id: `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }),
    onSuccess: (created) => {
      setReplyTo(null);
      // Upsert into timeline cache — do NOT invalidate (that races delivered/read ticks)
      queryClient.setQueryData(
        queryKeys.chatTimeline(activeConversationId!),
        (prev: { items: TimelineItem[]; dial_number: string } | undefined) => {
          if (!prev) {
            return {
              items: [
                {
                  id: `message:${created.id}`,
                  kind: "message" as const,
                  occurred_at: created.created_at,
                  message: { ...created, from_me: true, status: created.status || "sent" },
                },
              ],
              dial_number: "",
            };
          }
          const id = `message:${created.id}`;
          if (prev.items.some((item) => item.id === id)) {
            return {
              ...prev,
              items: prev.items.map((item) =>
                item.id === id && item.kind === "message"
                  ? {
                      ...item,
                      message: {
                        ...item.message,
                        ...created,
                        from_me: true,
                        status: item.message.status || created.status || "sent",
                      },
                    }
                  : item,
              ),
            };
          }
          return {
            ...prev,
            items: [
              ...prev.items,
              {
                id,
                kind: "message" as const,
                occurred_at: created.created_at,
                message: { ...created, from_me: true, status: created.status || "sent" },
              },
            ],
          };
        },
      );
      queryClient.setQueriesData(
        { queryKey: ["chat-conversations"] },
        (list: unknown) => {
          if (!Array.isArray(list)) return list;
          return list.map((conv: { id: string; preview?: string; unread?: number }) => {
            if (conv.id !== activeConversationId) return conv;
            return {
              ...conv,
              preview: created.body || created.attachment_name || conv.preview,
              time: created.created_at,
              last_message_at: created.created_at,
            };
          });
        },
      );
    },
  });

  // Track which conversation is actually on screen (for unread vs mark-read)
  useEffect(() => {
    if (!activeConversationId) {
      setViewingConversationId(null);
      return;
    }
    setViewingConversationId(activeConversationId);
    return () => {
      const current = useMessagesStore.getState().viewingConversationId;
      if (current === activeConversationId) {
        setViewingConversationId(null);
      }
    };
  }, [activeConversationId, setViewingConversationId]);

  const deleteMutation = useMutation({
    mutationFn: (payload: {
      message_id: string;
      action: "delete_me" | "delete_everyone";
    }) => chatMessageAction(activeConversationId!, payload),
    onSuccess: (_data, vars) => {
      if (vars.action === "delete_me") {
        queryClient.setQueryData(
          queryKeys.chatTimeline(activeConversationId!),
          (prev: { items: TimelineItem[]; dial_number: string } | undefined) => {
            if (!prev) return prev;
            return {
              ...prev,
              items: prev.items.filter(
                (item) =>
                  !(item.kind === "message" && item.message.id === vars.message_id),
              ),
            };
          },
        );
        toast.success("Message deleted");
      } else {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.chatTimeline(activeConversationId!),
        });
        toast.success("Deleted for everyone");
      }
      void queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
    },
    onError: () => toast.error("Could not delete message"),
  });

  useEffect(() => {
    setDraft("");
  }, [activeConversationId, setDraft]);

  useEffect(() => {
    if (!activeConversationId) return;
    void markChatRead(activeConversationId).then(() => {
      void queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
    });
  }, [activeConversationId, queryClient]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [timelineData?.items?.length, activeConversationId, peerTyping]);

  useEffect(() => {
    if (!activeConversationId) return;
    const text = draft.trim();
    if (!text) {
      if (lastTypingSentRef.current) {
        lastTypingSentRef.current = 0;
        void sendChatTyping(activeConversationId, false).catch(() => undefined);
      }
      return;
    }

    const now = Date.now();
    if (now - typingPulseRef.current < 1800) return;
    typingPulseRef.current = now;
    lastTypingSentRef.current = now;
    void sendChatTyping(activeConversationId, true).catch(() => undefined);

    const stopTimer = window.setTimeout(() => {
      if (Date.now() - lastTypingSentRef.current >= 2500) {
        void sendChatTyping(activeConversationId, false).catch(() => undefined);
        lastTypingSentRef.current = 0;
      }
    }, 2800);

    return () => window.clearTimeout(stopTimer);
  }, [draft, activeConversationId]);

  const dialNumber = timelineData?.dial_number || conversation?.dial_number || "";

  const visibleItems = useMemo(() => {
    const items = timelineData?.items || [];
    if (!threadSearchOpen || !threadSearch.trim()) return items;
    const hitIds = new Set(searchHits.map((m) => m.id));
    return items.filter(
      (item) => item.kind === "message" && hitIds.has(item.message.id),
    );
  }, [timelineData?.items, threadSearchOpen, threadSearch, searchHits]);

  const typingActive =
    Boolean(peerTyping) && (peerTyping?.expiresAt ?? 0) > Date.now();

  if (!activeConversationId) {
    return <ChatWelcomePanel />;
  }

  if (conversationLoading || !conversation) {
    return (
      <section className="flex h-full min-h-0 min-w-0 flex-1 items-center justify-center bg-surface text-sm text-fg-muted">
        Loading conversation…
      </section>
    );
  }

  const startCall = () => {
    if (!dialNumber) return;
    setDialNumber(dialNumber);
    openDialer();
    if (regStatus === "online") {
      makeCall(dialNumber);
    }
  };

  const sendMessage = () => {
    const text = draft.trim();
    if (!text || sendMutation.isPending) return;
    setDraft("");
    if (lastTypingSentRef.current) {
      lastTypingSentRef.current = 0;
      void sendChatTyping(activeConversationId, false).catch(() => undefined);
    }
    sendMutation.mutate({ body: text });
  };

  const statusLine = typingActive
    ? `${peerTyping?.name || "Someone"} is typing…`
    : conversation.type === "group"
      ? `${conversation.members?.length || 0} members`
      : conversation.online
        ? "Online"
        : dialNumber || "Offline";

  return (
    <div className="relative flex h-full min-h-0 min-w-0 flex-1 overflow-hidden">
      <section className="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-surface">
        <header className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-2 py-2.5 sm:gap-3 sm:px-4 sm:py-3">
          <button
            type="button"
            onClick={toggleContactInfo}
            className="flex min-w-0 flex-1 items-center gap-2 text-left sm:gap-3"
          >
            {showBack ? (
              <span
                role="button"
                tabIndex={0}
                aria-label="Back to conversations"
                onClick={(event) => {
                  event.stopPropagation();
                  onBack?.();
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.stopPropagation();
                    onBack?.();
                  }
                }}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-fg-muted hover:bg-surface-muted lg:hidden"
              >
                <ArrowLeft className="h-4 w-4" />
              </span>
            ) : null}
            <ConversationAvatar conversation={conversation} size="sm" />
            <span className="min-w-0 flex-1 overflow-hidden">
              <span className="block truncate text-sm font-semibold text-fg">
                {conversation.name}
              </span>
              <span
                className={cn(
                  "flex items-center gap-1.5 truncate text-xs",
                  typingActive ? "font-medium text-papaya-leaf" : "text-fg-muted",
                )}
              >
                {typingActive ? <TypingDots /> : null}
                <span className="truncate">{statusLine}</span>
              </span>
            </span>
          </button>
          <div className="flex shrink-0 items-center gap-0.5 text-fg-muted sm:gap-1">
            {dialNumber ? (
              <IconAction label="Voice call" onClick={startCall}>
                <Phone className="h-4 w-4" />
              </IconAction>
            ) : null}
            <IconAction
              label="Search"
              onClick={() => setThreadSearchOpen(!threadSearchOpen)}
            >
              <Search className="h-4 w-4" />
            </IconAction>
            <IconAction label="More" onClick={toggleContactInfo}>
              <MoreVertical className="h-4 w-4" />
            </IconAction>
          </div>
        </header>

        {threadSearchOpen ? (
          <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-fg-subtle" />
            <input
              value={threadSearch}
              onChange={(e) => setThreadSearch(e.target.value)}
              placeholder="Search in chat…"
              className="h-9 min-w-0 flex-1 bg-transparent text-sm outline-none"
              autoFocus
            />
            <button
              type="button"
              aria-label="Close search"
              onClick={() => setThreadSearchOpen(false)}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-surface-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : null}

        <div className="chat-wallpaper min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-3 py-4 sm:px-6 sm:py-5">
          {isLoading ? (
            <p className="text-center text-sm text-fg-muted">Loading…</p>
          ) : (
            <div className="flex w-full flex-col gap-3">
              {visibleItems.map((item) => (
                <TimelineRow
                  key={item.id}
                  item={item}
                  onReply={(message) => setReplyTo(message)}
                  onForward={(message) => setForwardMessage(message)}
                  onDeleteMe={(message) =>
                    deleteMutation.mutate({
                      message_id: message.id,
                      action: "delete_me",
                    })
                  }
                  onDeleteEveryone={(message) =>
                    deleteMutation.mutate({
                      message_id: message.id,
                      action: "delete_everyone",
                    })
                  }
                />
              ))}
              {typingActive ? (
                <div className="flex justify-start">
                  <div className="inline-flex items-center gap-1.5 rounded-[6px] border border-border bg-bubble-in px-3 py-2 shadow-sm">
                    <TypingDots />
                    <span className="text-xs text-fg-muted">typing</span>
                  </div>
                </div>
              ) : null}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <footer className="shrink-0 border-t border-border bg-surface p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:p-4">
          {replyTo ? (
            <div className="mb-2 flex items-start gap-2 rounded-[6px] border border-papaya-leaf/30 bg-papaya-leaf/5 px-3 py-2">
              <CornerUpLeft className="mt-0.5 h-3.5 w-3.5 shrink-0 text-papaya-leaf" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-papaya-leaf">
                  Replying to {replyTo.sender?.name || "message"}
                </p>
                <p className="truncate text-xs text-fg-muted">
                  {replyTo.body || replyTo.attachment_name || "Attachment"}
                </p>
              </div>
              <button
                type="button"
                aria-label="Cancel reply"
                onClick={() => setReplyTo(null)}
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-fg-muted hover:bg-surface-muted"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}
          <div className="flex w-full items-center gap-1.5 rounded-[6px] border border-border bg-surface-muted px-1.5 py-1.5 sm:gap-2 sm:px-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) sendMutation.mutate({ file, body: draft.trim() || undefined });
                event.target.value = "";
              }}
            />
            <button
              type="button"
              aria-label="Attach"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] text-fg-muted hover:bg-surface"
            >
              <Paperclip className="h-4 w-4" />
            </button>
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage();
                }
              }}
              placeholder={replyTo ? "Write a reply..." : "Type a message..."}
              className="h-9 min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle"
            />
            <button
              type="button"
              aria-label="Send"
              onClick={sendMessage}
              disabled={!draft.trim() || sendMutation.isPending}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] bg-papaya-leaf text-white hover:bg-papaya-leaf/90 disabled:opacity-40"
            >
              <SendHorizontal className="h-4 w-4" />
            </button>
          </div>
        </footer>
      </section>

      {contactInfoOpen ? (
        <ContactInfoDrawer
          conversation={conversation}
          dialNumber={dialNumber}
          timelineItems={timelineData?.items || []}
          onClose={() => setContactInfoOpen(false)}
          onCall={startCall}
          onSearch={() => {
            setContactInfoOpen(false);
            setThreadSearchOpen(true);
          }}
        />
      ) : null}

      <ForwardMessageModal />
    </div>
  );
}

function TimelineRow({
  item,
  onReply,
  onForward,
  onDeleteMe,
  onDeleteEveryone,
}: {
  item: TimelineItem;
  onReply: (message: ChatMessage) => void;
  onForward: (message: ChatMessage) => void;
  onDeleteMe: (message: ChatMessage) => void;
  onDeleteEveryone: (message: ChatMessage) => void;
}) {
  if (item.kind === "call") {
    const missed =
      item.call.direction === "missed" ||
      (item.call.direction === "incoming" && !item.call.answered);
    return (
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-surface/80 px-3 py-1.5 text-xs text-fg-muted shadow-sm backdrop-blur">
          {missed ? (
            <PhoneMissed className="h-3.5 w-3.5 text-red-500" />
          ) : (
            <PhoneOutgoing className="h-3.5 w-3.5" />
          )}
          <span>{callBubbleLabel(item.call)}</span>
          <span className="text-fg-subtle">{formatChatTime(item.occurred_at)}</span>
        </div>
      </div>
    );
  }

  if (item.kind === "note") {
    return (
      <div className="flex justify-center py-1">
        <div className="relative max-w-[88%] overflow-hidden rounded-[10px] border border-amber-500/25 bg-gradient-to-br from-amber-50 to-orange-50/80 px-3.5 py-3 shadow-[0_6px_18px_rgba(180,83,9,0.08)] sm:max-w-[72%] dark:border-amber-400/20 dark:from-amber-950/40 dark:to-orange-950/30">
          <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-amber-400 to-orange-500" />
          <div className="mb-1.5 flex items-center justify-between gap-3 pl-1.5">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-amber-800 uppercase dark:text-amber-200">
              <StickyNote className="h-3.5 w-3.5" />
              Call note
            </span>
            <span className="text-[10px] text-amber-800/70 dark:text-amber-200/60">
              {formatChatTime(item.occurred_at)}
            </span>
          </div>
          <p className="pl-1.5 whitespace-pre-wrap text-sm leading-relaxed text-fg">
            {item.note.content}
          </p>
          {item.note.author_name ? (
            <p className="mt-2 pl-1.5 text-[11px] text-amber-900/55 dark:text-amber-100/50">
              by {item.note.author_name}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <MessageBubble
      message={item.message}
      onReply={onReply}
      onForward={onForward}
      onDeleteMe={onDeleteMe}
      onDeleteEveryone={onDeleteEveryone}
    />
  );
}

function MessageBubble({
  message,
  onReply,
  onForward,
  onDeleteMe,
  onDeleteEveryone,
}: {
  message: ChatMessage;
  onReply: (message: ChatMessage) => void;
  onForward: (message: ChatMessage) => void;
  onDeleteMe: (message: ChatMessage) => void;
  onDeleteEveryone: (message: ChatMessage) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const fromMe = Boolean(message.from_me);
  const deleted = Boolean(message.is_deleted);
  const forwarded =
    Boolean(message.forwarded) || Boolean(message.meta?.forwarded);
  const replyPreview = normalizeReply(message.reply_to);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  return (
    <div className={cn("group flex", fromMe ? "justify-end" : "justify-start")}>
      <div className={cn("relative max-w-[85%] sm:max-w-[70%]", fromMe ? "items-end" : "items-start")}>
        <div
          className={cn(
            "rounded-[6px] px-3.5 py-2.5 text-sm shadow-sm",
            deleted
              ? "border border-dashed border-border bg-surface-muted/70 italic text-fg-muted"
              : fromMe
                ? "bg-bubble-out text-fg"
                : "border border-border bg-bubble-in text-fg",
          )}
        >
          {forwarded && !deleted ? (
            <p className="mb-1 inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide text-fg-subtle uppercase">
              <Forward className="h-3 w-3" />
              Forwarded
              {typeof message.meta?.forwarded_from_name === "string"
                ? ` · ${message.meta.forwarded_from_name}`
                : ""}
            </p>
          ) : null}

          {replyPreview && !deleted ? (
            <div className="mb-2 rounded-[4px] border-l-2 border-papaya-leaf bg-black/5 px-2 py-1.5 dark:bg-white/5">
              <p className="text-[10px] font-semibold text-papaya-leaf">
                {replyPreview.is_deleted
                  ? "Deleted message"
                  : replyPreview.sender_name || "Reply"}
              </p>
              <p className="line-clamp-2 text-xs text-fg-muted">
                {replyPreview.is_deleted
                  ? "This message was deleted"
                  : replyPreview.body || "Attachment"}
              </p>
            </div>
          ) : null}

          {deleted ? (
            <p className="text-sm">This message was deleted</p>
          ) : (
            <>
              {message.msg_type === "image" && message.attachment_url ? (
                <a href={message.attachment_url} target="_blank" rel="noreferrer">
                  <img
                    src={message.attachment_url}
                    alt={message.attachment_name || "image"}
                    className="mb-2 max-h-56 rounded-[4px] object-cover"
                  />
                </a>
              ) : null}
              {message.msg_type === "file" || message.msg_type === "audio" ? (
                <a
                  href={message.attachment_url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="mb-2 inline-flex items-center gap-2 rounded-[4px] bg-surface/50 px-2 py-1.5 text-xs"
                >
                  <FileText className="h-4 w-4" />
                  {message.attachment_name || "Attachment"}
                </a>
              ) : null}
              {message.body ? (
                <p className="whitespace-pre-wrap leading-relaxed">
                  {linkifyText(message.body)}
                </p>
              ) : null}
            </>
          )}

          <div
            className={cn(
              "mt-1 flex items-center gap-1 text-[10px] text-fg-subtle",
              fromMe ? "justify-end" : "justify-start",
            )}
          >
            <span>{formatChatTime(message.created_at)}</span>
            {fromMe && !deleted ? <StatusTicks status={message.status} /> : null}
          </div>
        </div>

        {!deleted ? (
          <div
            ref={menuRef}
            className={cn(
              "absolute top-0 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100",
              fromMe ? "-left-9" : "-right-9",
              menuOpen && "opacity-100",
            )}
          >
            <button
              type="button"
              aria-label="Message actions"
              onClick={() => setMenuOpen((open) => !open)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-surface text-fg-muted shadow-sm hover:bg-surface-muted"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
            {menuOpen ? (
              <div
                className={cn(
                  "absolute top-8 z-20 w-44 overflow-hidden rounded-[8px] border border-border bg-surface py-1 shadow-lg",
                  fromMe ? "right-0" : "left-0",
                )}
              >
                <MenuItem
                  icon={<CornerUpLeft className="h-3.5 w-3.5" />}
                  label="Reply"
                  onClick={() => {
                    setMenuOpen(false);
                    onReply(message);
                  }}
                />
                {(message.body || message.attachment_name) && (
                  <MenuItem
                    icon={<Forward className="h-3.5 w-3.5" />}
                    label="Forward"
                    onClick={() => {
                      setMenuOpen(false);
                      onForward(message);
                    }}
                  />
                )}
                <MenuItem
                  icon={<Trash2 className="h-3.5 w-3.5" />}
                  label="Delete for me"
                  onClick={() => {
                    setMenuOpen(false);
                    onDeleteMe(message);
                  }}
                />
                {fromMe ? (
                  <MenuItem
                    icon={<Trash2 className="h-3.5 w-3.5" />}
                    label="Delete for everyone"
                    danger
                    onClick={() => {
                      setMenuOpen(false);
                      onDeleteEveryone(message);
                    }}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatusTicks({ status }: { status?: string }) {
  if (status === "read") {
    return (
      <span title="Seen" aria-label="Seen">
        <CheckCheck className="h-3.5 w-3.5 text-sky-500" />
      </span>
    );
  }
  if (status === "delivered") {
    return (
      <span title="Delivered" aria-label="Delivered">
        <CheckCheck className="h-3.5 w-3.5 text-fg-subtle" />
      </span>
    );
  }
  return (
    <span title="Sent" aria-label="Sent">
      <Check className="h-3.5 w-3.5 text-fg-subtle" />
    </span>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface-muted",
        danger ? "text-danger" : "text-fg",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function normalizeReply(
  value: ChatMessage["reply_to"],
): MessageReplyPreview | null {
  if (!value) return null;
  if (typeof value === "string") {
    return { id: value, body: "" };
  }
  return value;
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-0.5" aria-hidden>
      <span className="h-1 w-1 animate-bounce rounded-full bg-current [animation-delay:-0.2s]" />
      <span className="h-1 w-1 animate-bounce rounded-full bg-current [animation-delay:-0.1s]" />
      <span className="h-1 w-1 animate-bounce rounded-full bg-current" />
    </span>
  );
}

function IconAction({
  label,
  children,
  onClick,
}: {
  label: string;
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-surface-muted"
    >
      {children}
    </button>
  );
}
